import React, { useCallback, useEffect, useRef } from 'react';
import { clamp } from 'lodash';
import styled from '@emotion/styled';
import { useAppSelector } from '@/redux/hooks';
import type { Milliseconds } from '@/redux/slices/packagingSlice.types';
import type { SegmentService as PlayerAPI } from '@accurate-player/accurate-player-segment';
import { selectPackagingTimelineFrameDuration, selectPackagingTimelineScale, selectPlayerCurrentFrame } from '@/redux/selectors/packagingSelectors';
import { selectTimelineFramesCount } from '@/redux/selectors/packagingTimelineSelectors';
import { MILLISECONDS_PER_PIXEL, PLAY_HEAD_VERTICAL_LINE_ZINDEX } from '@/components/Packaging/constants';
import { playerSeek } from '@/components/Packaging/utils';

interface Props {
  playerApiRef: React.MutableRefObject<PlayerAPI | null>;
}

const PlayHead: React.FC<Props> = ({ playerApiRef }) => {
  const currentFrame = useAppSelector(selectPlayerCurrentFrame);
  const frameDuration = useAppSelector(selectPackagingTimelineFrameDuration);
  const framesCount = useAppSelector(selectTimelineFramesCount);
  const scale = useAppSelector(selectPackagingTimelineScale);
  const timePosition: Milliseconds = currentFrame * frameDuration;
  const position = (timePosition * scale) / MILLISECONDS_PER_PIXEL;

  const moveStartPositionRef = useRef<number | null>(null);
  const moveStartFrameRef = useRef<number | null>(null);
  const handlePointerMoveRef = useRef<((e: PointerEvent) => void) | null>(null);

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      moveStartPositionRef.current = e.clientX;
      moveStartFrameRef.current = currentFrame;
      document.body.style.cursor = 'ew-resize';
    },
    [currentFrame]
  );

  /**
   * Why I decided to store pointer move and pointer up handlers inside refs rather than use functions wrapped with
   * useCallback? Move handler depends on `scale`, that mean we need to add `scale` to useCallback dependencies array.
   * If we do that move handler function will be re-created everytime scale change. So we will also have to add
   * move handler function to useEffect dependencies, to make sure move handler always use latest scale. That would mean
   * every time the scale change react will remove and again attach listeners to the body. To avoid that I decided
   * to use handlePointerMoveRef.
   */
  handlePointerMoveRef.current = (e: PointerEvent) => {
    if (!playerApiRef.current || moveStartPositionRef.current === null || moveStartFrameRef.current === null) {
      return;
    }

    const moveOffset = e.clientX - moveStartPositionRef.current;
    const newFrameIndex = clamp(
      moveStartFrameRef.current + Math.floor((moveOffset * MILLISECONDS_PER_PIXEL) / (scale * frameDuration)),
      0,
      framesCount
    );
    playerSeek({
      playerApi: playerApiRef.current,
      frameIndex: newFrameIndex
    });
  };

  const handlePointerUp = useCallback(() => {
    if (moveStartPositionRef.current !== null) {
      moveStartPositionRef.current = null;
    }
    if (moveStartFrameRef.current !== null) {
      moveStartFrameRef.current = null;
    }
    document.body.style.cursor = 'initial';
  }, []);

  useEffect(() => {
    const moveHandler = (e: PointerEvent) => handlePointerMoveRef.current!(e);
    document.body.addEventListener('pointermove', moveHandler);
    document.body.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.body.removeEventListener('pointermove', moveHandler);
      document.body.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  return (
    <Wrapper onPointerDown={handlePointerDown} data-prevent-timeline-seek>
      <PlayHeadStyled style={{ left: `${position}px` }}>
        <CornerClip>
          <Corner />
        </CornerClip>
      </PlayHeadStyled>
      <VerticalLineWrapper style={{ left: `${position + 1}px` }}>
        <VerticalLine />
      </VerticalLineWrapper>
    </Wrapper>
  );
};

export default PlayHead;

const playHeadColorDark = '255, 228, 112';
const playHeadColorLight = '255, 207, 0';
const playHeadBorderColorDark = `rgba(${playHeadColorDark}, 0.8)`;
const playHeadBgColorDark = `rgba(${playHeadColorDark}, 0.5)`;
const playHeadBorderColorLight = `rgba(${playHeadColorLight}, 0.9)`;
const playHeadBgColorLight = `rgba(${playHeadColorLight}, 0.6)`;

const Wrapper = styled.div`
  cursor: ew-resize;
`;

const PlayHeadStyled = styled.div`
  position: absolute;
  top: -30px;
  left: 1px;
  width: 9px;
  height: 18px;
  border-style: solid;
  border-color: ${({ theme }) => (theme.palette.mode === 'dark' ? playHeadBorderColorDark : playHeadBorderColorLight)};
  border-width: 1px 1px 0 1px;
  border-radius: 2px 2px 0 0;
  background-color: ${({ theme }) => (theme.palette.mode === 'dark' ? playHeadBgColorDark : playHeadBgColorLight)};
`;

const VerticalLineWrapper = styled.div`
  position: absolute;
  top: -9px;
  bottom: 0;
  z-index: ${PLAY_HEAD_VERTICAL_LINE_ZINDEX};
  padding: 0 3px;
`;

const VerticalLine = styled.div`
  height: 100%;
  border-left: 1px solid ${({ theme }) => (theme.palette.mode === 'dark' ? playHeadBorderColorDark : playHeadBorderColorLight)};
`;

const CornerClip = styled.div`
  position: absolute;
  left: -1px;
  top: 17px;
  width: 9px;
  height: 10px;
  overflow: hidden;
`;

const Corner = styled.div`
  position: absolute;
  width: 9px;
  height: 8px;
  border-style: solid;
  border-color: ${({ theme }) => (theme.palette.mode === 'dark' ? playHeadBorderColorDark : playHeadBorderColorLight)};
  border-width: 0 1px 1px 0;
  background-color: ${({ theme }) => (theme.palette.mode === 'dark' ? playHeadBgColorDark : playHeadBgColorLight)};
  transform-origin: center;
  transform: rotate(45deg) translateX(-46%) translateY(-46%);
`;
